import { GithubLogo, XLogo, DiscordLogo } from "@phosphor-icons/react";
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from "motion/react";
import { useState, useRef } from "react";
import logoLockup from "@/assets/images/basilica-logo-lockup.svg";
import { BasilicaLogo } from "@/components/basilica-logo";
import { cn } from "@/lib/utils";

function useScrollDirection(containerRef: React.RefObject<HTMLDivElement | null>) {
	const [hidden, setHidden] = useState(false);
	const { scrollY } = useScroll({ container: containerRef });
	const lastScrollY = useRef(0);

	useMotionValueEvent(scrollY, "change", (latest) => {
		const previous = lastScrollY.current;
		const direction = latest > previous ? "down" : "up";
		if (direction === "down" && latest > 100) {
			setHidden(true);
		} else if (direction === "up") {
			setHidden(false);
		}
		lastScrollY.current = latest;
	});

	return hidden;
}

function Button({ children, onClick, className }: { children: React.ReactNode; onClick?: () => void; className?: string }) {
	return (
		<motion.button
			onClick={onClick}
			className={cn(
				"group relative flex items-center justify-center gap-2.5 rounded-xl",
				"transition-all duration-100 px-4 py-2 h-[40px]",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30",
				"bg-white/10 hover:bg-white/90",
				className
			)}
		>
			<span className="font-sans font-normal text-[14px] transition-colors duration-100 text-white group-hover:text-[#0a0a0a]">
				{children}
			</span>
		</motion.button>
	);
}

export function PageLayout({ children, activeRoute }: { children: React.ReactNode; activeRoute?: string }) {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const headerHidden = useScrollDirection(scrollContainerRef);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	const navLinks = [
		{ href: "/about", label: "About Us" },
		{ href: "/blog", label: "Blog" },
		{ href: "/faqs", label: "FAQs" },
	];

	return (
		<div ref={scrollContainerRef} className="relative h-full w-full overflow-y-auto overflow-x-hidden bg-[#0a0a0a] text-white pt-5">
			<motion.header
				initial={{ y: 0 }}
				animate={{ y: headerHidden ? "-150%" : 0 }}
				transition={{ duration: 0.4, ease: [0.7, 0, 0.2, 1] }}
				className="sticky top-0 z-40 px-4 md:px-[8%] lg:px-[15%] py-3"
			>
				<div className="relative bg-[#050505]/80 backdrop-blur-xl rounded-xl overflow-hidden">
					<div
						className="pointer-events-none absolute inset-0 opacity-[0.08]"
						style={{
							backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
						}}
					/>
					<nav className="relative flex items-center justify-between px-5 py-3">
						<motion.a
							href="/"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.5 }}
							className="flex items-center"
						>
							<img src={logoLockup} alt="Basilica" className="h-[22px]" />
						</motion.a>

						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.5, delay: 0.1 }}
							className="hidden md:flex items-center gap-10"
						>
							{navLinks.map((link) => (
								<a
									key={link.href}
									href={link.href}
									className={cn(
										"text-[15px] transition-colors",
										activeRoute === link.href ? "text-white" : "text-white/60 hover:text-white"
									)}
								>
									{link.label}
								</a>
							))}
							<Button onClick={() => {
								window.location.href = "/#get-access";
							}}>
								Get Access
							</Button>
						</motion.div>

						<motion.button
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.5, delay: 0.1 }}
							onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
							className="md:hidden relative w-8 h-8 flex items-center justify-center"
							aria-label="Toggle menu"
						>
							<motion.span
								animate={mobileMenuOpen ? { rotate: 45, y: 0 } : { rotate: 0, y: -3 }}
								transition={{ duration: 0.2 }}
								className="absolute w-5 h-[1.5px] bg-white/80 rounded-full"
							/>
							<motion.span
								animate={mobileMenuOpen ? { rotate: -45, y: 0 } : { rotate: 0, y: 3 }}
								transition={{ duration: 0.2 }}
								className="absolute w-5 h-[1.5px] bg-white/80 rounded-full"
							/>
						</motion.button>
					</nav>

					<AnimatePresence>
						{mobileMenuOpen && (
							<motion.div
								initial={{ height: 0, opacity: 0 }}
								animate={{ height: "auto", opacity: 1 }}
								exit={{ height: 0, opacity: 0 }}
								transition={{ duration: 0.25, ease: [0.25, 1, 0.5, 1] }}
								className="md:hidden overflow-hidden"
							>
								<div className="px-5 pb-4 pt-1 flex flex-col">
									{navLinks.map((link) => (
										<a
											key={link.href}
											href={link.href}
											onClick={() => setMobileMenuOpen(false)}
											className={cn(
												"flex items-center py-3 text-[15px] transition-colors",
												activeRoute === link.href ? "text-white" : "text-white/60 hover:text-white"
											)}
										>
											{link.label}
										</a>
									))}
								</div>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</motion.header>

			{children}

			<footer className="relative mt-16 md:mt-24 pt-8 pb-12 md:pb-20 px-9 md:px-[8%] lg:px-[15%] border-t border-white/5">
				<div className="flex flex-col md:flex-row items-center justify-between gap-6">
					<div className="flex items-center gap-2">
						<BasilicaLogo size={16} className="text-white/40" />
						<span className="text-[15px] text-white/40">© 2025 Basilica Digital Inc.</span>
					</div>
					<div className="flex items-center gap-5">
						<a href="/privacy" className="text-[15px] text-white/40 hover:text-white transition-colors duration-150">Privacy</a>
						<a href="/terms" className="text-[15px] text-white/40 hover:text-white transition-colors duration-150">Terms</a>
						<a href="https://github.com/basilica" className="group text-white/40 hover:text-white transition-colors duration-150">
							<GithubLogo size={20} weight="regular" className="group-hover:hidden" />
							<GithubLogo size={20} weight="fill" className="hidden group-hover:block" />
						</a>
						<a href="https://discord.gg/basilica" className="group text-white/40 hover:text-white transition-colors duration-150">
							<DiscordLogo size={20} weight="regular" className="group-hover:hidden" />
							<DiscordLogo size={20} weight="fill" className="hidden group-hover:block" />
						</a>
						<a href="https://x.com/basilica" className="group text-white/40 hover:text-white transition-colors duration-150">
							<XLogo size={20} weight="regular" className="group-hover:hidden" />
							<XLogo size={20} weight="fill" className="hidden group-hover:block" />
						</a>
					</div>
				</div>
			</footer>
		</div>
	);
}
